import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ViewType, TabType, SearchMode, TeamFilterState } from '../../pages/Resource/types';
import { Sprint } from "../../services/organization";

// Helper function to get localStorage items with default values
const getLocalStorageItem = (key: string, defaultValue: any) => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
        return defaultValue;
    }
};

export type SearchType = 'people' | 'skills' | 'project' | 'task' | 'team' | 'sprint';

export type SprintSortType = 'name_asc' | 'name_desc' | 'latest' | 'oldest';

export interface CapacityPlanningFilters {
    searchType: SearchType;
    selectedResources: string[];
    selectedSkills: string[];
    selectedTeam: string; // For team filter mode (single select)
    selectedTeams: string[]; // For sprint filter mode (multiselect)
    selectedSprint: string; // Composite key "name::status" for sprint filter mode (e.g. "Sprint 1::current")
    sprintSortBy: SprintSortType; // Sort option for sprint dropdown
    sprintRangeSelection: 'current' | 'past-current' | 'current-future' | 'all'; // Sprint range selection for bucket view
    searchText: string;
    filteredResourceIds: number[];
    sprintFilters?: {
        skills: (string | number)[]; // Skill IDs
        work_item_type: string[]; // ["task", "user story", "bug", "spike"]
    };
}

// Helper function to get org-specific localStorage key
const getCapacityPlanningFiltersKey = (orgId?: string | number | null): string => {
    if (orgId) {
        return `capacityPlanningFilters_${orgId}`;
    }
    // Fallback to old key for backward compatibility (will be migrated on first save)
    return 'capacityPlanningFilters';
};

// Helper function to load capacity planning filters from localStorage (org-specific)
const loadCapacityPlanningFilters = (orgId?: string | number | null): CapacityPlanningFilters => {
    const key = getCapacityPlanningFiltersKey(orgId);
    const stored = getLocalStorageItem(key, null);
    if (stored) {
        return {
            searchType: stored.searchType || 'people',
            selectedResources: stored.selectedResources || [],
            selectedSkills: stored.selectedSkills || [],
            selectedTeam: stored.selectedTeam || '',
            selectedTeams: stored.selectedTeams || [],
            selectedSprint: stored.selectedSprint || '',
            sprintSortBy: stored.sprintSortBy || 'latest',
            sprintRangeSelection: 'current', // Always default to 'current', don't persist
            searchText: stored.searchText || '',
            filteredResourceIds: stored.filteredResourceIds || [],
            sprintFilters: stored.sprintFilters || { skills: [], work_item_type: [] },
        } as CapacityPlanningFilters;
    }
    return {
        searchType: 'people' as SearchType,
        selectedResources: [] as string[],
        selectedSkills: [] as string[],
        selectedTeam: '',
        selectedTeams: [] as string[],
        selectedSprint: '',
        sprintSortBy: 'latest' as SprintSortType,
        sprintRangeSelection: 'current' as 'current' | 'past-current' | 'current-future' | 'all',
        searchText: '',
        filteredResourceIds: [] as number[],
        sprintFilters: { skills: [], work_item_type: [] },
    } as CapacityPlanningFilters;
};

// Helper function to load team filter state from localStorage
// Persists sprint start_date and end_date when sprint is found (path present), null otherwise
// sprint_mode is true when sprint dates are present, false when null
const loadTeamFilterState = (): Partial<TeamFilterState> => {
    const stored = getLocalStorageItem('teamFilterState', null);
    if (stored) {
        // If sprint dates are stored, sprint_mode was true; if null, sprint_mode was false
        const hasSprint = stored.sprint_start_date && stored.sprint_end_date;
        return {
            team_id: stored.team_id || '',
            team_uuid: stored.team_uuid || '',
            team_name: stored.team_name || '',
            team_project: stored.team_project || '',
            team_project_name: stored.team_project_name || '',
            area_path: stored.area_path || '',
            sprint_mode: hasSprint || false,
            // Store sprint dates for restoration (will be used to find matching sprint)
            sprint_start_date: stored.sprint_start_date || null,
            sprint_end_date: stored.sprint_end_date || null,
            // Don't restore sprint objects/indexes - these need to be recalculated
            current_sprint: null,
            sorted_sprints: [],
            current_sprint_index: -1,
        };
    }
    return {
        team_id: '',
        team_uuid: '',
        team_name: '',
        team_project: '',
        team_project_name: '',
        area_path: '',
        sprint_mode: false,
        sprint_start_date: null,
        sprint_end_date: null,
        current_sprint: null,
        sorted_sprints: [],
        current_sprint_index: -1,
    };
};

export interface CapacityPlanningPayload {
    start_date: string;
    end_date: string;
    resource_ids: number[];
    filter_type: 'team' | 'resource' | 'sprint';
    // Array of team objects - each team has its own separate entry
    teams: Array<{
        team_name: string;
        project_name: string | null;
        path: string | null;
    }> | null;
    sprint_name?: string | null;
    sprint_type?: string | null;
}

export const resourceSlice = createSlice({
    name: 'resources',
    initialState: {
        data: [],
        loading: false,
        error: null,
        showResourcesEdit: false,
        resourcesbyIdsLoading: false,
        resourcesbyIdsError: null,
        resourcesData: {},
        skill_resources: [],
        skill_resources_Loading: false,
        skill_resources_Error: null,
        capacityPlanningData: {},
        capacityPlanningLoading: false,
        capacityPlanningError: null,
        manageResourcesLoading: false,
        manageResourcesError: null,
        suggestedTasks: {},
        suggestedTasksLoading: false,
        suggestedTasksError: null,
        unassignedTasksLoading: false,
        unassignedTasksError: null,
        unassignedTasksList: [],
        uatl: [],
        utilizationFilters: {
            debouncedSearchText: '',
            currentDateRange: null,
            isInitialized: false,
        },
        capacityPlanningFilters: loadCapacityPlanningFilters(null), // Will be loaded with org_id when available
        capacityPlanningPayload: null as CapacityPlanningPayload | null,
        shouldFetchCapacityPlanning: false,
        // Resource page UI state
        resourcePageState: {
            currentView: getLocalStorageItem('resource-currentView', ViewType.CAPACITY),
            activeTab: TabType.PEOPLE,
            searchTerm: getLocalStorageItem('resource-searchTerm', ''),
            searchMode: getLocalStorageItem('resource-searchMode', SearchMode.NAME),
        },
        resourcePageSprints: [] as Sprint[],
        teamSprints: [] as Sprint[],
        // Team filter state for capacity utilization (loaded from localStorage)
        teamFilterState: {
            ...loadTeamFilterState(),
        } as TeamFilterState,
        isUnallocatedDrawerOpen: false,
    },
    reducers: {
        setResources: (state, action) => {
            state.data = action.payload
        },
        setResourcesLoading: (state, action) => {
            state.loading = action.payload
        },
        setResourcesError: (state, action) => {
            state.error = action.payload
            state.capacityPlanningData = []
        },
        setShowResourcesEdit: (state, action) => {
            state.showResourcesEdit = action.payload
        },
        setResourcesbyIdsLoading: (state, action) => {
            state.resourcesbyIdsLoading = action.payload
        },
        setResourcesbyIdsError: (state, action) => {
            state.resourcesbyIdsError = action.payload
        },
        setResourcesbyIds: (state, action) => {
            state.resourcesData = {
                ...state.resourcesData,
                ...action.payload
            }
        },
        setResourcesbySkill: (state, action) => {
            state.skill_resources = action.payload.resources
        },
        setResourcesbySkillLoading: (state, action) => {
            state.skill_resources_Loading = action.payload
        },
        setResourcesbySkillError: (state, action) => {
            state.skill_resources_Error = action.payload
        },
        setCapacityPlanningRes: (state, action) => {
            state.capacityPlanningData = action.payload
        },
        setCapacityPlanningLoading: (state, action) => {
            state.capacityPlanningLoading = action.payload
        },
        setCapacityPlanningError: (state, action) => {
            state.capacityPlanningError = action.payload
        },
        setManageResourcesLoading: (state, action) => {
            state.manageResourcesLoading = action.payload
        },
        setManageResourcesError: (state, action) => {
            state.manageResourcesError = action.payload
        },
        setSuggestedTasksRes: (state, action) => {
            state.suggestedTasks = action.payload
        },
        setSuggestedTasksLoading: (state, action) => {
            state.suggestedTasksLoading = action.payload
        },
        setSuggestedTasksError: (state, action) => {
            state.suggestedTasksError = action.payload
        },
        setResetUnassignedTasks: (state) => {
            state.capacityPlanningData = []
        },
        setUnassignedTasksLoading: (state, action) => {
            state.unassignedTasksLoading = action.payload
        },
        setUnassignedTasksError: (state, action) => {
            state.unassignedTasksError = action.payload
            state.capacityPlanningData = []
        },
        setUnassignedTasks: (state, action) => {
            state.unassignedTasksList = action.payload
        },
        setUnassignedAdditionalTasks: (state, action) => {
            state.uatl = action.payload
        },
        // Utilization filter actions
        setUtilizationDebouncedSearchText: (state, action) => {
            state.utilizationFilters.debouncedSearchText = action.payload
        },
        setUtilizationCurrentDateRange: (state, action) => {
            state.utilizationFilters.currentDateRange = action.payload
        },
        setUtilizationIsInitialized: (state, action) => {
            state.utilizationFilters.isInitialized = action.payload
        },
        resetUtilizationFilters: (state) => {
            state.utilizationFilters = {
                debouncedSearchText: '',
                currentDateRange: null,
                isInitialized: false,
            }
        },
        loadUtilizationFiltersFromStorage: (state, action) => {
            state.utilizationFilters = { ...state.utilizationFilters, ...action.payload }
        },
        // Resource page UI state reducers
        setResourceCurrentView: (state, action: PayloadAction<ViewType>) => {
            state.resourcePageState.currentView = action.payload;
            localStorage.setItem('resource-currentView', JSON.stringify(action.payload));
        },
        setResourceActiveTab: (state, action: PayloadAction<TabType>) => {
            state.resourcePageState.activeTab = action.payload;
        },
        setResourceSearchTerm: (state, action: PayloadAction<string>) => {
            state.resourcePageState.searchTerm = action.payload;
            localStorage.setItem('resource-searchTerm', JSON.stringify(action.payload));
        },
        setResourceSearchMode: (state, action: PayloadAction<SearchMode>) => {
            state.resourcePageState.searchMode = action.payload;
            localStorage.setItem('resource-searchMode', JSON.stringify(action.payload));
        },
        resetResourcePageState: (state) => {
            state.resourcePageState = {
                currentView: ViewType.CAPACITY,
                activeTab: TabType.PEOPLE,
                searchTerm: '',
                searchMode: SearchMode.NAME,
            };
            localStorage.removeItem('resource-searchTerm');
            localStorage.removeItem('resource-searchMode');
            localStorage.removeItem('resource-currentView');
        },
        setResourcesSprints: (state, action: PayloadAction<Sprint[]>) => {
            state.resourcePageSprints = action.payload;
        },
        resetResourcesSprints: (state) => {
            state.resourcePageSprints = [];
        },
        setTeamSprints: (state, action: PayloadAction<Sprint[]>) => {
            state.teamSprints = action.payload;
        },
        setUnallocatedDrawerOpen: (state, action: PayloadAction<boolean>) => {
            state.isUnallocatedDrawerOpen = action.payload;
        },
        resetTeamSprints: (state) => {
            state.teamSprints = [];
        },
        // Team filter actions for capacity utilization
        setTeamFilterState: (state, action: PayloadAction<TeamFilterState>) => {
            state.teamFilterState = action.payload;
            // Persist basic team info and sprint dates (when path is present, sprint is found)
            try {
                const hasSprint = action.payload.area_path && action.payload.current_sprint;
                const toPersist = {
                    team_id: action.payload.team_id,
                    team_uuid: action.payload.team_uuid,
                    team_name: action.payload.team_name,
                    team_project: action.payload.team_project,
                    team_project_name: action.payload.team_project_name,
                    area_path: action.payload.area_path,
                    sprint_mode: hasSprint,
                    // Store sprint dates when sprint is found (path present), null otherwise
                    sprint_start_date: hasSprint ? action.payload.current_sprint?.start_date : null,
                    sprint_end_date: hasSprint ? action.payload.current_sprint?.end_date : null,
                };
                localStorage.setItem('teamFilterState', JSON.stringify(toPersist));
            } catch (error) {
                console.warn('Failed to save teamFilterState to localStorage:', error);
            }
        },
        updateTeamFilterState: (state, action: PayloadAction<Partial<TeamFilterState>>) => {
            state.teamFilterState = { ...state.teamFilterState, ...action.payload };
            // Persist basic team info and sprint dates whenever it's updated
            try {
                const hasSprint = state.teamFilterState.area_path && state.teamFilterState.current_sprint;
                const toPersist = {
                    team_id: state.teamFilterState.team_id,
                    team_uuid: state.teamFilterState.team_uuid,
                    team_name: state.teamFilterState.team_name,
                    team_project: state.teamFilterState.team_project,
                    team_project_name: state.teamFilterState.team_project_name,
                    area_path: state.teamFilterState.area_path,
                    sprint_mode: hasSprint,
                    // Store sprint dates when sprint is found (path present), null otherwise
                    sprint_start_date: hasSprint ? state.teamFilterState.current_sprint?.start_date : null,
                    sprint_end_date: hasSprint ? state.teamFilterState.current_sprint?.end_date : null,
                };
                localStorage.setItem('teamFilterState', JSON.stringify(toPersist));
            } catch (error) {
                console.warn('Failed to save teamFilterState to localStorage:', error);
            }
        },
        resetTeamFilterState: (state) => {
            state.teamFilterState = {
                team_id: '',
                team_uuid: '',
                team_name: '',
                team_project: '',
                team_project_name: '',
                area_path: '',
                current_sprint: null,
                sprint_mode: false,
                sorted_sprints: [],
                current_sprint_index: -1,
            };
            // Clear from localStorage
            try {
                localStorage.removeItem('teamFilterState');
            } catch (error) {
                console.warn('Failed to remove teamFilterState from localStorage:', error);
            }
        },
        setCurrentSprint: (state, action: PayloadAction<Sprint | null>) => {
            state.teamFilterState.current_sprint = action.payload;
            state.teamFilterState.sprint_mode = action.payload !== null;
            state.teamFilterState.area_path = action.payload?.path || '';
            // Persist sprint dates when sprint is found (path present), null otherwise
            try {
                const hasSprint = action.payload !== null && action.payload.path;
                const toPersist = {
                    team_id: state.teamFilterState.team_id,
                    team_uuid: state.teamFilterState.team_uuid,
                    team_name: state.teamFilterState.team_name,
                    team_project: state.teamFilterState.team_project,
                    team_project_name: state.teamFilterState.team_project_name,
                    area_path: state.teamFilterState.area_path,
                    sprint_mode: hasSprint,
                    sprint_start_date: hasSprint ? action.payload?.start_date : null,
                    sprint_end_date: hasSprint ? action.payload?.end_date : null,
                };
                localStorage.setItem('teamFilterState', JSON.stringify(toPersist));
            } catch (error) {
                console.warn('Failed to save teamFilterState to localStorage:', error);
            }
        },
        setSortedSprintsWithIndex: (state, action: PayloadAction<{ sprints: Sprint[], index: number }>) => {
            state.teamFilterState.sorted_sprints = action.payload.sprints;
            state.teamFilterState.current_sprint_index = action.payload.index;
            if (action.payload.index >= 0 && action.payload.sprints[action.payload.index]) {
                const selectedSprint = action.payload.sprints[action.payload.index];
                // Only set sprint_mode if sprint has valid dates
                if (selectedSprint.start_date && selectedSprint.end_date) {
                    state.teamFilterState.current_sprint = selectedSprint;
                    state.teamFilterState.sprint_mode = true;
                    state.teamFilterState.area_path = selectedSprint.path || '';
                } else {
                    // Sprint has null dates - reset sprint mode
                    state.teamFilterState.current_sprint = null;
                    state.teamFilterState.sprint_mode = false;
                    state.teamFilterState.area_path = '';
                }
            } else {
                state.teamFilterState.current_sprint = null;
                state.teamFilterState.sprint_mode = false;
                state.teamFilterState.area_path = '';
            }
            // Persist sprint dates when sprint is found (path present), null otherwise
            try {
                const hasSprint = state.teamFilterState.area_path && state.teamFilterState.current_sprint;
                const toPersist = {
                    team_id: state.teamFilterState.team_id,
                    team_uuid: state.teamFilterState.team_uuid,
                    team_name: state.teamFilterState.team_name,
                    team_project: state.teamFilterState.team_project,
                    team_project_name: state.teamFilterState.team_project_name,
                    area_path: state.teamFilterState.area_path,
                    sprint_mode: hasSprint,
                    sprint_start_date: hasSprint ? state.teamFilterState.current_sprint?.start_date : null,
                    sprint_end_date: hasSprint ? state.teamFilterState.current_sprint?.end_date : null,
                };
                localStorage.setItem('teamFilterState', JSON.stringify(toPersist));
            } catch (error) {
                console.warn('Failed to save teamFilterState to localStorage:', error);
            }
        },
        navigateToSprintIndex: (state, action: PayloadAction<number>) => {
            const index = action.payload;
            if (index >= 0 && index < state.teamFilterState.sorted_sprints.length) {
                state.teamFilterState.current_sprint_index = index;
                state.teamFilterState.current_sprint = state.teamFilterState.sorted_sprints[index];
                state.teamFilterState.area_path = state.teamFilterState.sorted_sprints[index].path || '';
                // Persist sprint dates when sprint is found (path present)
                try {
                    const hasSprint = state.teamFilterState.area_path && state.teamFilterState.current_sprint;
                    const toPersist = {
                        team_id: state.teamFilterState.team_id,
                        team_uuid: state.teamFilterState.team_uuid,
                        team_name: state.teamFilterState.team_name,
                        team_project: state.teamFilterState.team_project,
                        team_project_name: state.teamFilterState.team_project_name,
                        area_path: state.teamFilterState.area_path,
                        sprint_mode: hasSprint,
                        sprint_start_date: hasSprint ? state.teamFilterState.current_sprint?.start_date : null,
                        sprint_end_date: hasSprint ? state.teamFilterState.current_sprint?.end_date : null,
                    };
                    localStorage.setItem('teamFilterState', JSON.stringify(toPersist));
                } catch (error) {
                    console.warn('Failed to save teamFilterState to localStorage:', error);
                }
            }
        },
        setCapacityPlanningFilters: (state, action: PayloadAction<{ filters: CapacityPlanningFilters; orgId?: string | number | null }>) => {
            state.capacityPlanningFilters = action.payload.filters;
            // Persist to localStorage with org-specific key
            const orgId = action.payload.orgId;
            const key = getCapacityPlanningFiltersKey(orgId);
            try {
                localStorage.setItem(key, JSON.stringify(action.payload.filters));
                // Remove old key if org_id is provided (migration)
                if (orgId) {
                    try {
                        localStorage.removeItem('capacityPlanningFilters');
                    } catch (e) {
                        // Ignore errors during migration
                    }
                }
            } catch (error) {
                console.warn('Failed to save capacityPlanningFilters to localStorage:', error);
            }
        },
        updateCapacityPlanningFilters: (state, action: PayloadAction<{ filters: Partial<CapacityPlanningFilters>; orgId?: string | number | null }>) => {
            // Update with new values - preserve all existing filter values
            state.capacityPlanningFilters = { ...state.capacityPlanningFilters, ...action.payload.filters };

            // Persist to localStorage with org-specific key
            const orgId = action.payload.orgId;
            const key = getCapacityPlanningFiltersKey(orgId);
            try {
                // Create a copy without sprintRangeSelection for persistence
                const filtersToPersist = { ...state.capacityPlanningFilters };
                delete (filtersToPersist as any).sprintRangeSelection;
                localStorage.setItem(key, JSON.stringify(filtersToPersist));
                // Remove old key if org_id is provided (migration)
                if (orgId) {
                    try {
                        localStorage.removeItem('capacityPlanningFilters');
                    } catch (e) {
                        // Ignore errors during migration
                    }
                }
            } catch (error) {
                console.warn('Failed to save capacityPlanningFilters to localStorage:', error);
            }
        },
        resetCapacityPlanningFilters: (state, action?: PayloadAction<{ orgId?: string | number | null }>) => {
            state.capacityPlanningFilters = {
                searchType: 'people' as SearchType,
                selectedResources: [],
                selectedSkills: [],
                selectedTeam: '',
                selectedTeams: [],
                selectedSprint: '',
                sprintSortBy: 'latest' as SprintSortType,
                searchText: '',
                filteredResourceIds: [],
                sprintFilters: { skills: [], work_item_type: [] },
                sprintRangeSelection: 'all'
            };
            // Remove from localStorage with org-specific key
            const orgId = action?.payload?.orgId;
            const key = getCapacityPlanningFiltersKey(orgId);
            try {
                localStorage.removeItem(key);
                // Also remove old key for backward compatibility
                if (orgId) {
                    try {
                        localStorage.removeItem('capacityPlanningFilters');
                    } catch (e) {
                        // Ignore errors
                    }
                }
            } catch (error) {
                console.warn('Failed to remove capacityPlanningFilters from localStorage:', error);
            }
        },
        loadCapacityPlanningFiltersForOrg: (state, action: PayloadAction<string | number | null>) => {
            // Load filters for specific org
            state.capacityPlanningFilters = loadCapacityPlanningFilters(action.payload);
        },
        setCapacityPlanningPayload: (state, action: PayloadAction<CapacityPlanningPayload | null>) => {
            state.capacityPlanningPayload = action.payload;
        },
        triggerCapacityPlanningFetch: (state) => {
            state.shouldFetchCapacityPlanning = true;
        },
        resetCapacityPlanningFetchTrigger: (state) => {
            state.shouldFetchCapacityPlanning = false;
        },
    }
});

export const {
    setResources,
    setResourcesLoading,
    setResourcesError,
    setShowResourcesEdit,
    setResourcesbyIdsLoading,
    setResourcesbyIdsError,
    setResourcesbyIds,
    setResourcesbySkill,
    setResourcesbySkillLoading,
    setResourcesbySkillError,
    setCapacityPlanningRes,
    setCapacityPlanningLoading,
    setCapacityPlanningError,
    setManageResourcesLoading,
    setManageResourcesError,
    setUnassignedTasksLoading,
    setUnassignedTasksError,
    setUnassignedTasks,
    setResetUnassignedTasks,
    setSuggestedTasksRes,
    setUnassignedAdditionalTasks,
    setUtilizationDebouncedSearchText,
    setUtilizationCurrentDateRange,
    setUtilizationIsInitialized,
    resetUtilizationFilters,
    loadUtilizationFiltersFromStorage,
    setResourceCurrentView,
    setResourceActiveTab,
    setResourceSearchTerm,
    setResourceSearchMode,
    resetResourcePageState,
    setSuggestedTasksLoading,
    setSuggestedTasksError,
    setResourcesSprints,
    resetResourcesSprints,
    setTeamSprints,
    resetTeamSprints,
    setTeamFilterState,
    updateTeamFilterState,
    resetTeamFilterState,
    setCurrentSprint,
    setSortedSprintsWithIndex,
    navigateToSprintIndex,
    setCapacityPlanningFilters,
    updateCapacityPlanningFilters,
    resetCapacityPlanningFilters,
    loadCapacityPlanningFiltersForOrg,
    setCapacityPlanningPayload,
    triggerCapacityPlanningFetch,
    resetCapacityPlanningFetchTrigger,
    setUnallocatedDrawerOpen,
} = resourceSlice.actions;

export default resourceSlice.reducer;